import {
  membershipCdnIndex as membershipCdnIndexPure,
  readRegionFromToken,
  resolveMembershipCdnUrl as resolveMembershipCdnUrlPure,
} from './regional-cdn'

export { membershipGcsObjectPath, readRegionFromToken } from './regional-cdn'

/** Lazy-load Nuxt request APIs so media-layer → regional-host stays safe during Vitest/Nuxt config load. */
function loadNuxtRequestApis(): {
  tryUseNuxtApp: typeof import('nuxt/app').tryUseNuxtApp
  useRequestURL: typeof import('nuxt/app').useRequestURL
  useRequestHeaders: typeof import('nuxt/app').useRequestHeaders
} | null {
  try {
    // Sync require keeps preLoginRegionHeader synchronous for SSR URL rewriting.
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    return require('nuxt/app')
  } catch {
    return null
  }
}

export const AU_STAGING_SERVICES_ORIGIN = 'https://staging-au.services.leadconnectorhq.com'
export const AU_PRODUCTION_SERVICES_ORIGIN = 'https://au.services.leadconnectorhq.com'

/** US assets-drm API bases → AU hosts (matches @ghl-revex/assets-drm-client region map). */
const ASSETS_DRM_API_BY_US_ORIGIN: Record<string, string> = {
  'https://staging.backend.leadconnectorhq.com/assets-drm': 'https://staging-au.backend.leadconnectorhq.com/assets-drm',
  'https://backend.leadconnectorhq.com/assets-drm': 'https://au.backend.leadconnectorhq.com/assets-drm',
}

const AU_PORTAL_HOST_SUFFIXES = ['.staging-au.clientclub.net', '.app-au.clientclub.net']

/** US services origins rewritten to AU hosts for document downloads only. */
const DOCUMENT_DOWNLOAD_SERVICES_ORIGIN_TO_AU: Record<string, string> = {
  'https://staging.services.leadconnectorhq.com': AU_STAGING_SERVICES_ORIGIN,
  'https://services.leadconnectorhq.com': AU_PRODUCTION_SERVICES_ORIGIN,
}

export const AU_STAGING_FIREBASE = {
  apiKey: 'AIzaSyC-JM4q8GB3BQ4XfBmcJ-8Lj2OJK6mEp1E',
  authDomain: 'hl-data-stg-au.firebaseapp.com',
  projectId: 'hl-data-stg-au',
  storageBucket: 'hl-data-stg-au.firebasestorage.app',
  messagingSenderId: '103276149391',
  appId: '1:103276149391:web:e737f125aeef4568e90efa',
}

/**
 * Rewrites a services/backend URL for the given data region.
 * Extend the AU branch for production / interop hosts when those hostnames are decided.
 */
export function toRegionalUrl(url: string, region?: string): string {
  if (region !== 'au' || !url) return url

  // Staging AU: staging.services → staging-au.services
  if (url.includes('staging-au')) return url
  if (url.includes('staging')) return url.replace('staging', 'staging-au')

  // Production AU: add mapping when the AU production services host is known.
  // Interop AU: add mapping when the AU interop services host is known.
  return url
}

export function isAuPortalHost(hostname?: string): boolean {
  const host = (hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')).toLowerCase()
  return AU_PORTAL_HOST_SUFFIXES.some(suffix => host.includes(suffix))
}

/** `*.staging-au.clientclub.net` only. Production AU hosts do not get `x-dr-region`. */
export function isAuStagingPortalHost(hostname?: string): boolean {
  const host = (hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')).toLowerCase()
  return host.includes('.staging-au.clientclub.net')
}

/** Public clientclub calls that stay on staging.services and send `x-dr-region` before login. */
export function isClientclubPreLoginPath(url?: string): boolean {
  if (!url) return false
  const path = url.split(/[?#]/)[0]
  return (
    /\/portal-settings$/.test(path) ||
    /\/auth\/login\/email$/.test(path) ||
    /\/auth\/login\/otp$/.test(path) ||
    /\/users\/forgot-password$/.test(path) ||
    /\/auth\/forgot-password$/.test(path)
  )
}

/**
 * Rewrites base URL for AU data region when the call needs a regional host.
 *
 * - assets-drm API: staging/prod backend assets-drm → AU backend assets-drm
 * - document downloads: staging/prod services → AU services
 * - other endpoints keep the US/interop host and rely on `x-dr-region`
 */
export function resolveRegionalBaseUrl(url: string, endpoint: string, token?: string | null, dataRegion?: string): string {
  if (!url) return url

  const assetsDrmUsOrigin = Object.keys(ASSETS_DRM_API_BY_US_ORIGIN).find(origin => url === origin || url.startsWith(`${origin}/`))
  if (assetsDrmUsOrigin) {
    const isAu = dataRegion?.toLowerCase() === 'au' || readRegionFromToken(token) === 'au' || preLoginRegionHeader(endpoint, token) === 'au'
    if (!isAu) return url
    return url.replace(assetsDrmUsOrigin, ASSETS_DRM_API_BY_US_ORIGIN[assetsDrmUsOrigin])
  }

  if (!/^\/documents\/download(\/|$)/.test(endpoint)) {
    return url
  }

  const isAu = dataRegion?.toLowerCase() === 'au' || preLoginRegionHeader(endpoint, token) === 'au'
  if (!isAu) {
    return url
  }

  for (const [usOrigin, auOrigin] of Object.entries(DOCUMENT_DOWNLOAD_SERVICES_ORIGIN_TO_AU)) {
    if (url === usOrigin || url.startsWith(`${usOrigin}/`)) {
      return url.replace(usOrigin, auOrigin)
    }
  }

  return url
}

function isAuHostValue(value?: string): boolean {
  return !!value && (isAuPortalHost(value) || isAuStagingPortalHost(value))
}

function isAuFromBrowserLocation(): boolean {
  if (typeof window === 'undefined') return false
  return isAuHostValue(window.location.hostname) || isAuHostValue(window.location.search)
}

function isAuFromNuxtRequestContext(): boolean {
  try {
    const nuxtApis = loadNuxtRequestApis()
    if (!nuxtApis) return false

    const nuxtApp = nuxtApis.tryUseNuxtApp()
    if (!nuxtApp) return false

    const reqUrl = nuxtApis.useRequestURL()
    if (isAuHostValue(reqUrl?.hostname) || isAuHostValue(reqUrl?.search)) return true

    const headers = nuxtApis.useRequestHeaders(['host', 'domain', 'x-forwarded-host', 'x-original-host'])
    const host = headers?.['domain'] || headers?.['x-forwarded-host'] || headers?.['x-original-host'] || headers?.['host']
    return isAuHostValue(host ? String(host) : undefined)
  } catch {
    return false
  }
}

/** `au` when on an AU clientclub host, when domain or endpoint targets AU, or when the token belongs to AU. */
export function preLoginRegionHeader(_endpoint?: string, token?: string | null, hostname?: string): 'au' | undefined {
  if (readRegionFromToken(token) === 'au') return 'au'
  if (isAuHostValue(hostname) || isAuHostValue(_endpoint)) return 'au'
  if (typeof window !== 'undefined') {
    return isAuFromBrowserLocation() ? 'au' : undefined
  }
  return isAuFromNuxtRequestContext() ? 'au' : undefined
}

function activeMembershipRegion(): 'us' | 'au' {
  return preLoginRegionHeader() === 'au' ? 'au' : 'us'
}

/** Rewrites a membership CDN URL to the AU host when the active region is AU. US URLs are unchanged. */
export function resolveMembershipCdnUrl(url?: string, region: 'us' | 'au' = activeMembershipRegion()): string {
  return resolveMembershipCdnUrlPure(url, region)
}

/** Index of the US or AU membership CDN host inside a URL. -1 when neither host is present. */
export function membershipCdnIndex(assetUrl: string, usCdnUrl: string): number {
  return membershipCdnIndexPure(assetUrl, usCdnUrl, activeMembershipRegion())
}
